(() => {
const pricingDefaults = {
  voyager3StandardListPrice: 399,
  voyager3PlusListPrice: 549,
  partnerDiscountPercent: 10,
};

const automationDefaults = {
  hoursPerIncidentWithAutomation: 0.5,
  incidentsPreventedPerServerPerYear: 0.5,
};

function numberOrZero(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundTo(value, digits = 0) {
  const multiplier = 10 ** digits;
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
}

function getAutomationLicenseCost(deviceCount) {
  const count = numberOrZero(deviceCount);

  if (count <= 0) {
    return 0;
  }

  if (count <= 3) {
    return 149;
  }

  if (count <= 20) {
    return 799;
  }

  if (count <= 50) {
    return 1999;
  }

  return count * 40;
}

function calculateMspMargin(inputs) {
  const monthlyManagementFee = numberOrZero(inputs.monthlyManagementFee);
  const serverCount = numberOrZero(inputs.serverCount);
  const incidentsPerServerPerYear = numberOrZero(inputs.incidentsPerServerPerYear);
  const truckRollsPerServerPerYear = numberOrZero(inputs.truckRollsPerServerPerYear);
  const hoursWithoutTinyPilot = numberOrZero(
    inputs.hoursPerIncidentWithoutTinyPilot,
  );
  const hoursWithTinyPilot = numberOrZero(inputs.hoursPerIncidentWithTinyPilot);
  const technicianCostPerHour = numberOrZero(inputs.technicianCostPerHour);
  const costPerTruckRoll = numberOrZero(inputs.costPerTruckRoll);
  const deviceListPrice = numberOrZero(inputs.deviceListPrice);
  const deviceCount = numberOrZero(inputs.serverCount);
  const partnerDiscountPercent = numberOrZero(inputs.partnerDiscountPercent);
  const clientDeviceCharge = numberOrZero(inputs.clientDeviceCharge);
  const automationEnabled =
    inputs.automationEnabled === true || inputs.automationEnabled === "on";

  const annualManagementRevenue = monthlyManagementFee * 12 * serverCount;
  const partnerDeviceCost =
    deviceListPrice * deviceCount * (1 - partnerDiscountPercent / 100);

  const incidentsPreventedPerServer = automationEnabled
    ? automationDefaults.incidentsPreventedPerServerPerYear
    : 0;
  const activeIncidentsPerServer = Math.max(
    0,
    incidentsPerServerPerYear - incidentsPreventedPerServer,
  );
  const truckRollRatio =
    incidentsPerServerPerYear > 0
      ? truckRollsPerServerPerYear / incidentsPerServerPerYear
      : 0;
  const activeTruckRollsPerServer = activeIncidentsPerServer * truckRollRatio;

  const effectiveHoursWithTinyPilot = automationEnabled
    ? automationDefaults.hoursPerIncidentWithAutomation
    : hoursWithTinyPilot;
  const hoursSavedPerIncident = Math.max(
    0,
    hoursWithoutTinyPilot - effectiveHoursWithTinyPilot,
  );
  const totalIncidentsPerYear = activeIncidentsPerServer * serverCount;
  const totalTruckRollsPerYear = Math.min(
    activeTruckRollsPerServer * serverCount,
    totalIncidentsPerYear,
  );
  const annualLaborSavings =
    totalIncidentsPerYear * hoursSavedPerIncident * technicianCostPerHour;
  const avoidedTruckRolls = totalTruckRollsPerYear;
  const annualTruckRollSavings = totalTruckRollsPerYear * costPerTruckRoll;
  const automationLicenseCostPerYear = automationEnabled
    ? getAutomationLicenseCost(deviceCount)
    : 0;
  const totalAnnualMarginPreserved =
    annualLaborSavings + annualTruckRollSavings;
  const hardwareResaleMargin = Math.max(0, clientDeviceCharge - partnerDeviceCost);
  const unrecoveredDeviceCost = Math.max(0, partnerDeviceCost - clientDeviceCharge);
  const netYearOneMarginImpact =
    totalAnnualMarginPreserved +
    hardwareResaleMargin -
    unrecoveredDeviceCost -
    automationLicenseCostPerYear;
  const monthlyMarginPreserved = totalAnnualMarginPreserved / 12;
  const totalYearOneInvestment = unrecoveredDeviceCost + automationLicenseCostPerYear;

  return {
    annualManagementRevenue: roundTo(annualManagementRevenue, 2),
    partnerDeviceCost: roundTo(partnerDeviceCost, 2),
    annualLaborSavings: roundTo(annualLaborSavings, 2),
    annualTruckRollSavings: roundTo(annualTruckRollSavings, 2),
    automationLicenseCostPerYear: roundTo(automationLicenseCostPerYear, 2),
    automationEnabled,
    avoidedTruckRolls: roundTo(avoidedTruckRolls, 1),
    hoursSavedPerYear: roundTo(totalIncidentsPerYear * hoursSavedPerIncident, 1),
    totalAnnualMarginPreserved: roundTo(totalAnnualMarginPreserved, 2),
    netYearOneMarginImpact: roundTo(netYearOneMarginImpact, 2),
    paybackMonths:
      monthlyMarginPreserved > 0
        ? roundTo(totalYearOneInvestment / monthlyMarginPreserved, 1)
        : 0,
    breakEvenTruckRolls:
      costPerTruckRoll > 0 ? roundTo(partnerDeviceCost / costPerTruckRoll, 1) : 0,
    hardwareResaleMargin: roundTo(hardwareResaleMargin, 2),
  };
}

function calculateClientRoi(inputs) {
  const clientDevicePrice = numberOrZero(inputs.clientDevicePrice);
  const serverCount = numberOrZero(inputs.serverCount);
  const incidentsPerServerPerYear = numberOrZero(inputs.incidentsPerServerPerYear);
  const hoursSavedPerIncident = numberOrZero(inputs.hoursSavedPerIncident);
  const downtimeCostPerHour = numberOrZero(inputs.downtimeCostPerHour);
  const avoidedOnsiteVisitsPerServerPerYear = numberOrZero(
    inputs.avoidedOnsiteVisitsPerServerPerYear,
  );
  const clientTruckRollCharge = numberOrZero(inputs.clientTruckRollCharge);

  const totalInvestment = clientDevicePrice * serverCount;
  const totalIncidentsPerYear = incidentsPerServerPerYear * serverCount;
  const annualDowntimeValue =
    totalIncidentsPerYear * hoursSavedPerIncident * downtimeCostPerHour;
  const totalAvoidedOnsiteVisits =
    avoidedOnsiteVisitsPerServerPerYear * serverCount;
  const annualTruckRollAvoidance =
    totalAvoidedOnsiteVisits * clientTruckRollCharge;
  const totalAnnualClientValue = annualDowntimeValue + annualTruckRollAvoidance;
  const monthlyClientValue = totalAnnualClientValue / 12;

  return {
    totalInvestment: roundTo(totalInvestment, 2),
    annualDowntimeValue: roundTo(annualDowntimeValue, 2),
    annualTruckRollAvoidance: roundTo(annualTruckRollAvoidance, 2),
    totalAnnualClientValue: roundTo(totalAnnualClientValue, 2),
    paybackMonths:
      monthlyClientValue > 0 ? roundTo(totalInvestment / monthlyClientValue, 1) : 0,
    roiPercent:
      totalInvestment > 0
        ? roundTo(((totalAnnualClientValue - totalInvestment) / totalInvestment) * 100)
        : 0,
  };
}

const summaryMoneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const summaryDecimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

const modelLabels = {
  standard: "Voyager 3 Standard",
  plus: "Voyager 3 Plus (PoE + 2nd LAN)",
};

const HUBSPOT_FIELD_LEAD_SOURCE = "roi_calculator_lead_source";
const HUBSPOT_FIELD_SUBMISSION = "roi_calculator_submission";

const formRegistry = new Map();

function formatSummaryMoney(value) {
  return summaryMoneyFormatter.format(Number(value) || 0);
}

function formatSummaryDecimal(value) {
  return summaryDecimalFormatter.format(Number(value) || 0);
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

function modelLabel(value) {
  return modelLabels[value] || value || "Not specified";
}

function formatSubmittedAt() {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());
}

function joinLines(lines) {
  return lines.filter(Boolean).join("\n");
}

function buildMspSubmissionSummary(values, result) {
  return joinLines([
    "Calculator: MSP Margin Calculator",
    `Submitted: ${formatSubmittedAt()}`,
    "",
    "Assumptions:",
    `- Monthly server management fee: ${formatSummaryMoney(values.monthlyManagementFee)}`,
    `- Managed servers: ${values.serverCount || 0}`,
    `- Recovery incidents per server per year: ${formatSummaryDecimal(values.incidentsPerServerPerYear)}`,
    `- Truck rolls per server per year: ${formatSummaryDecimal(values.truckRollsPerServerPerYear)}`,
    `- Hours per incident today: ${formatSummaryDecimal(values.hoursPerIncidentWithoutTinyPilot)}`,
    `- Hours per incident with console access: ${formatSummaryDecimal(values.hoursPerIncidentWithTinyPilot)}`,
    `- Loaded technician cost/hr: ${formatSummaryMoney(values.technicianCostPerHour)}`,
    `- Cost per truck roll: ${formatSummaryMoney(values.costPerTruckRoll)}`,
    `- TinyPilot model: ${modelLabel(values.model)}`,
    `- List price per device: ${formatSummaryMoney(values.deviceListPrice)}`,
    `- Client charge for device (optional): ${formatSummaryMoney(values.clientDeviceCharge)}`,
    `- Automation License included: ${yesNo(values.automationEnabled)}`,
    `- Partner discount applied in results: ${pricingDefaults.partnerDiscountPercent}%`,
    "",
    "Results:",
    `- Annual management revenue: ${formatSummaryMoney(result.annualManagementRevenue)}`,
    `- Partner device cost: ${formatSummaryMoney(result.partnerDeviceCost)}`,
    `- Annual labor savings: ${formatSummaryMoney(result.annualLaborSavings)}`,
    `- Annual truck roll savings: ${formatSummaryMoney(result.annualTruckRollSavings)}`,
    `- Annual margin preserved: ${formatSummaryMoney(result.totalAnnualMarginPreserved)}`,
    `- Net year-one margin impact: ${formatSummaryMoney(result.netYearOneMarginImpact)}`,
    `- Payback period: ${formatSummaryDecimal(result.paybackMonths)} months`,
    `- Hours saved per year: ${formatSummaryDecimal(result.hoursSavedPerYear)}`,
    `- Onsite visits avoided: ${formatSummaryDecimal(result.avoidedTruckRolls)} per year`,
    `- Break-even truck rolls: ${formatSummaryDecimal(result.breakEvenTruckRolls)}`,
    result.automationEnabled
      ? `- Automation License cost (annual): ${formatSummaryMoney(result.automationLicenseCostPerYear)}`
      : null,
    Number(result.hardwareResaleMargin) > 0
      ? `- Hardware resale margin: ${formatSummaryMoney(result.hardwareResaleMargin)}`
      : null,
  ]);
}

function buildClientSubmissionSummary(values, result) {
  return joinLines([
    "Calculator: Downtime Cost Calculator",
    `Submitted: ${formatSubmittedAt()}`,
    "",
    "Assumptions:",
    `- Servers: ${values.serverCount || 0}`,
    `- TinyPilot model: ${modelLabel(values.model)}`,
    `- Price per device: ${formatSummaryMoney(values.clientDevicePrice)}`,
    `- Recovery incidents per server per year: ${formatSummaryDecimal(values.incidentsPerServerPerYear)}`,
    `- Hours saved per incident: ${formatSummaryDecimal(values.hoursSavedPerIncident)}`,
    `- Downtime cost per hour: ${formatSummaryMoney(values.downtimeCostPerHour)}`,
    `- Onsite visits avoided per server per year: ${formatSummaryDecimal(values.avoidedOnsiteVisitsPerServerPerYear)}`,
    `- Onsite visit charge: ${formatSummaryMoney(values.clientTruckRollCharge)}`,
    "",
    "Results:",
    `- Total investment: ${formatSummaryMoney(result.totalInvestment)}`,
    `- Downtime value recovered: ${formatSummaryMoney(result.annualDowntimeValue)}`,
    `- Onsite visit charges avoided: ${formatSummaryMoney(result.annualTruckRollAvoidance)}`,
    `- Annual business value: ${formatSummaryMoney(result.totalAnnualClientValue)}`,
    `- Payback period: ${formatSummaryDecimal(result.paybackMonths)} months`,
    `- First-year ROI: ${formatSummaryDecimal(result.roiPercent)}%`,
  ]);
}

function setDomField(form, name, value) {
  const field = form.querySelector(`input[name="${name}"], textarea[name="${name}"]`);
  if (!field) {
    return;
  }

  field.value = value;
  field.dispatchEvent(new Event("input", { bubbles: true }));
  field.dispatchEvent(new Event("change", { bubbles: true }));
}

function syncDomFields(form, leadSource, submission) {
  setDomField(form, HUBSPOT_FIELD_LEAD_SOURCE, leadSource);
  setDomField(form, HUBSPOT_FIELD_SUBMISSION, submission);
}

async function resolveV4FieldNames(hubspotForm) {
  const fieldNames = {
    leadSource: HUBSPOT_FIELD_LEAD_SOURCE,
    submission: HUBSPOT_FIELD_SUBMISSION,
  };

  if (typeof hubspotForm.getFormFieldValues !== "function") {
    return fieldNames;
  }

  try {
    const fields = await hubspotForm.getFormFieldValues();
    for (const field of fields) {
      const name = field.name || "";
      if (name.includes(HUBSPOT_FIELD_LEAD_SOURCE)) {
        fieldNames.leadSource = name;
      }
      if (name.includes(HUBSPOT_FIELD_SUBMISSION)) {
        fieldNames.submission = name;
      }
    }
  } catch (_) {
    // Fall back to property internal names.
  }

  return fieldNames;
}

function syncV4Fields(state) {
  const { form, fieldNames, leadSource, submissionSummary } = state;
  if (!form?.setFieldValue || !fieldNames) {
    return;
  }

  form.setFieldValue(fieldNames.leadSource, leadSource || "");
  form.setFieldValue(fieldNames.submission, submissionSummary || "");
}

function registerHubSpotV4Listeners() {
  if (window.__roiHubSpotV4Registered) {
    return;
  }

  window.__roiHubSpotV4Registered = true;

  window.addEventListener("hs-form-event:on-ready", async (event) => {
    const formId = event.detail?.formId;
    const hubspotForm = window.HubSpotFormsV4?.getFormFromEvent(event);
    if (!formId || !hubspotForm) {
      return;
    }

    const container = document.querySelector(`[data-hubspot-form-id="${formId}"]`);
    const root = container?.closest("[data-calculator-mode]");
    if (!container) {
      return;
    }

    const fieldNames = await resolveV4FieldNames(hubspotForm);
    const existing = formRegistry.get(formId) || {
      leadSource: root?.dataset.hubspotLeadSource || "",
      submissionSummary: "",
    };

    existing.form = hubspotForm;
    existing.fieldNames = fieldNames;
    formRegistry.set(formId, existing);
    syncV4Fields(existing);
  });

  document.addEventListener(
    "submit",
    (event) => {
      const formEl = event.target;
      if (!(formEl instanceof HTMLFormElement)) {
        return;
      }

      const container = formEl.closest("[data-hubspot-form]");
      if (!container) {
        return;
      }

      const state = formRegistry.get(container.dataset.hubspotFormId || "");
      if (state) {
        syncV4Fields(state);
        syncDomFields(formEl, state.leadSource, state.submissionSummary);
      }
    },
    true,
  );
}

registerHubSpotV4Listeners();

function initHubSpotLeadCapture(root) {
  const leadSource = root.dataset.hubspotLeadSource || "";
  const target = root.querySelector("[data-hubspot-form]");
  const formId = target?.dataset.hubspotFormId || "";

  if (!target) {
    return null;
  }

  let submissionSummary = "";

  function sync() {
    if (formId) {
      const state = formRegistry.get(formId);
      if (state) {
        state.leadSource = leadSource;
        state.submissionSummary = submissionSummary;
        syncV4Fields(state);
      }
    }

    const form = target.querySelector("form");
    if (form) {
      syncDomFields(form, leadSource, submissionSummary);
    }
  }

  function bindForm(form) {
    if (!form || form.dataset.roiHubspotBound) {
      return;
    }

    form.dataset.roiHubspotBound = "true";
    form.addEventListener("submit", sync, true);
    sync();
  }

  function update(summary) {
    submissionSummary = summary || "";
    sync();
  }

  if (formId) {
    const state = formRegistry.get(formId) || {
      leadSource,
      submissionSummary,
    };
    state.leadSource = leadSource;
    state.submissionSummary = submissionSummary;
    formRegistry.set(formId, state);
    syncV4Fields(state);
  }

  bindForm(target.querySelector("form"));

  const observer = new MutationObserver(() => {
    bindForm(target.querySelector("form"));
    sync();
  });
  observer.observe(target, { childList: true, subtree: true });

  return { update };
}

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

const modelPrices = {
  standard: pricingDefaults.voyager3StandardListPrice,
  plus: pricingDefaults.voyager3PlusListPrice,
};

function getFormValues(form) {
  const values = Object.fromEntries(
    Array.from(form.elements)
      .filter((element) => element.name)
      .map((element) => [element.name, element.value]),
  );

  if (form.elements.automationEnabled) {
    values.automationEnabled = form.elements.automationEnabled.checked;
  }

  return values;
}

function setText(root, name, value) {
  const element = root.querySelector(`[data-output="${name}"]`);
  if (element) {
    element.textContent = value;
  }
}

function money(value) {
  return moneyFormatter.format(Number(value) || 0);
}

function decimal(value) {
  return decimalFormatter.format(Number(value) || 0);
}

function syncModelPrice(form, priceFieldName) {
  const model = form.elements.model;
  const priceField = form.elements[priceFieldName];

  if (!model || !priceField) {
    return;
  }

  model.addEventListener("change", () => {
    priceField.value = modelPrices[model.value];
    form.dispatchEvent(new Event("input", { bubbles: true }));
  });
}

function renderMsp(root, form, leadCapture) {
  const values = getFormValues(form);
  const result = calculateMspMargin({
    ...values,
    partnerDiscountPercent: pricingDefaults.partnerDiscountPercent,
  });

  setText(root, "annual-management-revenue", money(result.annualManagementRevenue));
  setText(root, "partner-device-cost", money(result.partnerDeviceCost));
  setText(root, "annual-labor-savings", money(result.annualLaborSavings));
  setText(root, "annual-truck-roll-savings", money(result.annualTruckRollSavings));
  setText(
    root,
    "automation-license-cost",
    money(result.automationLicenseCostPerYear),
  );
  setText(root, "total-margin-preserved", money(result.totalAnnualMarginPreserved));
  setText(root, "net-year-one-impact", money(result.netYearOneMarginImpact));
  setText(root, "payback-months", `${decimal(result.paybackMonths)} months`);
  setText(
    root,
    "break-even-truck-rolls",
    `${decimal(result.breakEvenTruckRolls)} truck rolls`,
  );
  setText(root, "hours-saved", `${decimal(result.hoursSavedPerYear)} hours/year`);
  setText(
    root,
    "avoided-truck-rolls",
    `${decimal(result.avoidedTruckRolls)} per year`,
  );
  setText(root, "hardware-margin", money(result.hardwareResaleMargin));

  root.querySelectorAll("[data-automation-output]").forEach((element) => {
    element.hidden = !result.automationEnabled;
  });

  leadCapture?.update(buildMspSubmissionSummary(values, result));
}

function renderClient(root, form, leadCapture) {
  const values = getFormValues(form);
  const result = calculateClientRoi(values);

  setText(root, "total-investment", money(result.totalInvestment));
  setText(root, "annual-downtime-value", money(result.annualDowntimeValue));
  setText(root, "annual-truck-roll-avoidance", money(result.annualTruckRollAvoidance));
  setText(root, "total-client-value", money(result.totalAnnualClientValue));
  setText(root, "payback-months", `${decimal(result.paybackMonths)} months`);
  setText(root, "roi-percent", `${decimal(result.roiPercent)}%`);

  leadCapture?.update(buildClientSubmissionSummary(values, result));
}

function initCalculator(root) {
  const mode = root.dataset.calculatorMode;
  const form = root.querySelector(".calculator-shell form");
  const leadCapture = initHubSpotLeadCapture(root);

  if (!form) {
    return;
  }

  if (mode === "msp") {
    syncModelPrice(form, "deviceListPrice");
    const render = () => renderMsp(root, form, leadCapture);
    form.addEventListener("input", render);
    form.addEventListener("change", render);
    render();
    return;
  }

  if (mode === "client") {
    syncModelPrice(form, "clientDevicePrice");
    const render = () => renderClient(root, form, leadCapture);
    form.addEventListener("input", render);
    form.addEventListener("change", render);
    render();
  }
}

document
  .querySelectorAll("[data-calculator-mode]")
  .forEach((root) => initCalculator(root));
})();
